import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Upload,
  FileText,
  Download,
  Archive,
  ArchiveRestore,
  Search,
  Plus,
  File,
  User,
  Loader2,
  X,
  Tag,
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';
import {
  useDocumentsList,
  useUploadDocument,
  useGetDocumentDownloadUrl,
  useArchiveDocument,
  useRestoreDocument,
  useUpdateDocumentMetadata,
  validateDocumentFile,
  MAX_FILE_SIZE,
  ALLOWED_MIME_TYPES,
  type DocumentListItem,
} from '@/hooks/useDocuments';
import { Button } from '@/components/ui/Button';

// ── Patient picker helper ──────────────────────────────────────────────────────
function usePatientList(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: ['patients', 'dropdown', clinicId],
    enabled: Boolean(clinicId),
    queryFn: async () => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('patients')
        .select('id, first_name, last_name, pat_number')
        .eq('clinic_id', clinicId)
        .eq('status', 'active')
        .order('last_name', { ascending: true })
        .limit(500);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 60_000,
  });
}

// ── File size formatter ────────────────────────────────────────────────────────
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function mimeToIcon(mime: string): string {
  if (mime === 'application/pdf') return '📄';
  if (mime.startsWith('image/')) return '🖼️';
  if (mime.includes('word')) return '📝';
  if (mime.includes('sheet')) return '📊';
  if (mime.includes('presentation')) return '📋';
  return '📁';
}

// ── Upload Modal ──────────────────────────────────────────────────────────────
interface UploadModalProps {
  clinicId: string;
  initialPatientId?: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function UploadModal({ clinicId, initialPatientId, onClose, onSuccess }: UploadModalProps) {
  const { t } = useTranslation();
  const patients = usePatientList(clinicId);
  const upload = useUploadDocument();

  const [file, setFile] = useState<File | null>(null);
  const [patientId, setPatientId] = useState<string>(initialPatientId ?? '');
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [progress, setProgress] = useState(0);
  const [fileError, setFileError] = useState<string | null>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const handleFile = useCallback(
    (f: File) => {
      setFileError(null);
      const err = validateDocumentFile(f);
      if (err) {
        setFileError(t(`documents.${err}`));
        return;
      }
      setFile(f);
    },
    [t],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const f = e.dataTransfer.files[0];
      if (f) handleFile(f);
    },
    [handleFile],
  );

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
  };

  const handleSubmit = async () => {
    if (!file) return;
    try {
      await upload.mutateAsync({
        clinicId,
        patientId: patientId || null,
        file,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        notes: notes.trim() || null,
        onProgress: setProgress,
      });
      toast.success(t('documents.upload_success'));
      onSuccess();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg === 'size_limit_exceeded') toast.error(t('documents.size_limit_exceeded'));
      else if (msg === 'invalid_type') toast.error(t('documents.invalid_type'));
      else toast.error(t('documents.upload_failed'));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('documents.upload')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Dropzone */}
        <div
          ref={dropRef}
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => document.getElementById('doc-file-input')?.click()}
          className="mb-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-8 transition-colors hover:border-indigo-400 hover:bg-indigo-50 dark:border-gray-600 dark:bg-gray-700 dark:hover:border-indigo-500 dark:hover:bg-gray-600"
        >
          <input
            id="doc-file-input"
            type="file"
            className="hidden"
            accept={ALLOWED_MIME_TYPES.join(',')}
            onChange={onFileInput}
          />
          {file ? (
            <div className="text-center">
              <span className="text-3xl">{mimeToIcon(file.type)}</span>
              <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{file.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{formatBytes(file.size)}</p>
            </div>
          ) : (
            <div className="text-center">
              <Upload className="mx-auto h-10 w-10 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Drag & drop or click to select
              </p>
              <p className="mt-1 text-xs text-gray-400">
                PDF, images, Office docs · Max {formatBytes(MAX_FILE_SIZE)}
              </p>
            </div>
          )}
        </div>
        {fileError && <p className="mb-3 text-sm text-red-600 dark:text-red-400">{fileError}</p>}

        {/* Patient picker */}
        <div className="mb-3">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('documents.filter_patient')}
          </label>
          <select
            value={patientId}
            onChange={(e) => setPatientId(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          >
            <option value="">{t('documents.filter_patient')}</option>
            {(patients.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.first_name} {p.last_name} #{p.pat_number}
              </option>
            ))}
          </select>
        </div>

        {/* Tags */}
        <div className="mb-3">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('documents.tags_label')}
          </label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="consent, lab, invoice"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
          <p className="mt-0.5 text-xs text-gray-400">Comma-separated</p>
        </div>

        {/* Notes */}
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('documents.notes_label')}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
        </div>

        {/* Progress bar */}
        {upload.isPending && (
          <div className="mb-4">
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-600">
              <div
                className="h-full bg-indigo-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">{progress}%</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={upload.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!file || upload.isPending || Boolean(fileError)}>
            {upload.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading…
              </>
            ) : (
              t('documents.upload')
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Edit metadata modal ────────────────────────────────────────────────────────
interface EditModalProps {
  doc: DocumentListItem;
  onClose: () => void;
}

function EditModal({ doc, onClose }: EditModalProps) {
  const { t } = useTranslation();
  const update = useUpdateDocumentMetadata();
  const [tags, setTags] = useState(doc.tags.join(', '));
  const [notes, setNotes] = useState(doc.notes ?? '');

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        document_id: doc.id,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        notes: notes.trim() || null,
      });
      toast.success('Saved');
      onClose();
    } catch {
      toast.error('Could not save metadata');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Edit — {doc.filename}
          </h2>
          <button type="button" onClick={onClose}>
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        <div className="mb-3">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('documents.tags_label')}
          </label>
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t('documents.notes_label')}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={update.isPending}>
            {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Document row ──────────────────────────────────────────────────────────────
interface DocRowProps {
  doc: DocumentListItem;
  isAdmin: boolean;
  onDownload: (doc: DocumentListItem) => void;
  onArchive: (doc: DocumentListItem) => void;
  onRestore: (doc: DocumentListItem) => void;
  onEdit: (doc: DocumentListItem) => void;
}

function DocRow({ doc, isAdmin, onDownload, onArchive, onRestore, onEdit }: DocRowProps) {
  const { t } = useTranslation();
  const patientName = doc.patient_first_name
    ? `${doc.patient_first_name} ${doc.patient_last_name}`
    : null;
  const uploaderName = doc.uploader_first_name
    ? `${doc.uploader_first_name} ${doc.uploader_last_name}`
    : null;

  return (
    <div
      className={`flex items-center gap-4 rounded-lg border px-4 py-3 transition-colors ${
        doc.archived_at
          ? 'border-gray-200 bg-gray-50 opacity-70 dark:border-gray-700 dark:bg-gray-800/50'
          : 'border-gray-200 bg-white hover:border-indigo-200 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-indigo-600'
      }`}
    >
      <span className="text-2xl">{mimeToIcon(doc.mime_type)}</span>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
            {doc.filename}
          </p>
          {doc.archived_at && (
            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              Archived
            </span>
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
          <span>{formatBytes(doc.size_bytes)}</span>
          {patientName && (
            <span className="flex items-center gap-1">
              <User className="h-3 w-3" />
              {patientName}
            </span>
          )}
          {uploaderName && <span>by {uploaderName}</span>}
          <span>{format(new Date(doc.created_at), 'dd MMM yyyy')}</span>
        </div>
        {doc.tags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {doc.tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-0.5 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
              >
                <Tag className="h-2.5 w-2.5" />
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-1">
        {!doc.archived_at && (
          <button
            type="button"
            onClick={() => onDownload(doc)}
            title={t('documents.download')}
            className="rounded p-1.5 text-gray-400 hover:bg-indigo-50 hover:text-indigo-600 dark:hover:bg-indigo-900/30 dark:hover:text-indigo-400"
          >
            <Download className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => onEdit(doc)}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
        >
          <FileText className="h-4 w-4" />
        </button>
        {isAdmin &&
          (doc.archived_at ? (
            <button
              type="button"
              onClick={() => onRestore(doc)}
              title={t('documents.restore')}
              className="rounded p-1.5 text-gray-400 hover:bg-green-50 hover:text-green-600 dark:hover:bg-green-900/30 dark:hover:text-green-400"
            >
              <ArchiveRestore className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onArchive(doc)}
              title={t('documents.archive')}
              className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
            >
              <Archive className="h-4 w-4" />
            </button>
          ))}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export function Documents() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const [search, setSearch] = useState('');
  const [patientFilter, setPatientFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editingDoc, setEditingDoc] = useState<DocumentListItem | null>(null);

  const patients = usePatientList(activeClinicId);
  const docs = useDocumentsList(activeClinicId, {
    search,
    patientId: patientFilter || null,
    tags: tagFilter ? [tagFilter] : [],
    showArchived,
  });

  const downloadUrl = useGetDocumentDownloadUrl();
  const archive = useArchiveDocument();
  const restore = useRestoreDocument();

  const isAdmin = Boolean(isPlatformAdmin || (activeClinicId && isClinicAdmin(activeClinicId)));

  const handleDownload = async (doc: DocumentListItem) => {
    try {
      const url = await downloadUrl.mutateAsync(doc.id);
      window.open(url, '_blank');
    } catch {
      toast.error(t('documents.upload_failed'));
    }
  };

  const handleArchive = async (doc: DocumentListItem) => {
    if (!window.confirm(`Archive "${doc.filename}"?`)) return;
    try {
      await archive.mutateAsync(doc.id);
      toast.success(t('documents.archive'));
    } catch {
      toast.error('Could not archive document');
    }
  };

  const handleRestore = async (doc: DocumentListItem) => {
    try {
      await restore.mutateAsync(doc.id);
      toast.success(t('documents.restore'));
    } catch {
      toast.error('Could not restore document');
    }
  };

  return (
    <div className="mx-auto max-w-5xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('documents.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t('documents.no_documents')}
          </p>
        </div>
        <Button onClick={() => setShowUpload(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t('documents.upload')}
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-3">
        {/* Search */}
        <div className="relative min-w-48 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t('documents.search_placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
        </div>

        {/* Patient filter */}
        <select
          value={patientFilter}
          onChange={(e) => setPatientFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        >
          <option value="">{t('documents.filter_patient')}</option>
          {(patients.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.first_name} {p.last_name}
            </option>
          ))}
        </select>

        {/* Tag filter */}
        <input
          type="text"
          placeholder={t('documents.filter_tags')}
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
        />

        {/* Show archived toggle */}
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="rounded"
          />
          {t('documents.show_archived')}
        </label>
      </div>

      {/* Document list */}
      {docs.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : docs.data && docs.data.length > 0 ? (
        <div className="space-y-2">
          {docs.data.map((doc) => (
            <DocRow
              key={doc.id}
              doc={doc}
              isAdmin={isAdmin}
              onDownload={handleDownload}
              onArchive={handleArchive}
              onRestore={handleRestore}
              onEdit={setEditingDoc}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 py-20 dark:border-gray-600">
          <File className="h-12 w-12 text-gray-300 dark:text-gray-600" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            {t('documents.no_documents')}
          </p>
          <Button className="mt-4" onClick={() => setShowUpload(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('documents.upload')}
          </Button>
        </div>
      )}

      {/* Upload modal */}
      {showUpload && activeClinicId && (
        <UploadModal
          clinicId={activeClinicId}
          onClose={() => setShowUpload(false)}
          onSuccess={() => setShowUpload(false)}
        />
      )}

      {/* Edit metadata modal */}
      {editingDoc && <EditModal doc={editingDoc} onClose={() => setEditingDoc(null)} />}
    </div>
  );
}
